import { DateTime } from 'luxon'
import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  shouldDisplaAvailability,
  shouldDisplayEstimatedDeliveryDate,
  shouldDisplayEstimatedDeliveryDateForAus
} from './VariantCard.model'
import { ClinTheme } from '../../ClinTheme'
import { ClinButtonIcon } from '../../components/ClinButtonIcon'
import { EstimatedDeliveryDate } from '../../components/ClinEstimatedDeliveryDate/EstimatedDeliveryDate'
import { EstimatedDeliveryDateAus } from '../../components/ClinEstimatedDeliveryDate/EstimatedDeliveryDateAus'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinText } from '../../components/ClinText'
import { TypographyVariant } from '../../components/ClinText/ClinText.styles'
import { ClinTwoColumnCard } from '../../components/ClinTwoColumnCard'
import { CountryAlphaCodes } from '../../constants/countryAlpha2Codes'
import { ProductSourceSystem } from '../../pages/Products/Product.models'
import {
  IAugmentedCatalogItemDto,
  UIVariationState
} from '../../services/PortfolioJourneys/PortfolioJourney.types'
import { DrugPropertyGrid } from '../DrugPropertyGrid'

interface IVariantCardProps {
  /** Catalogue item to display */
  catalogueItem: IAugmentedCatalogItemDto & {
    displayOnRequest?: boolean
    shouldShowStock?: boolean
  }
  /** Expose onClick for card */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  /** Text for message that leads to variant/SKU details page */
  cta: string
  /** Shows a delete icon and calls this call back when the user clicks on it */
  onClickDelete?: () => void
  /** Whether to show an overlay saying the item has been deleted */
  isDeleted?: boolean
  /** Whether to show Minimum order message */
  showGBMinimumOrderMessage?: boolean

  countryCode?: string
}

/**
 * Greys out the card if the item isn't available
 * Calculates if the cat item is available or not
 * @param catalogueItem
 */
export const getUnavailableStatus = (
  catalogueItem: IAugmentedCatalogItemDto
) => {
  return (
    catalogueItem.cannotOrder &&
    !catalogueItem.canRegisterInterest &&
    !catalogueItem.canAccessSourcingRequestForm &&
    catalogueItem.uiVariantState !== UIVariationState.AvailableDistributor &&
    catalogueItem.uiVariantState !== UIVariationState.Undefined
  )
}

/**
 * Get the availability text to display, prioritizing live P&A data over ETL data
 * This matches the logic used in StatusColumn component
 */
const getAvailabilityText = (
  catalogueItem: IAugmentedCatalogItemDto
): string => {
  if (catalogueItem.displayOnRequest) {
    return 'Available'
  }

  // Check if we have live P&A data with available quantity
  const availableQuantity = catalogueItem.priceAvailability?.availableQuantity

  if (availableQuantity) {
    const quantity = Number(availableQuantity)
    if (quantity > 0) {
      return `${quantity} pack${quantity === 1 ? '' : 's'} available`
    }
  }

  // Fall back to ETL-based availability text
  return catalogueItem.availableText || 'Available'
}

/**
 * Get the availability color, prioritizing live P&A data over ETL data
 * This matches the logic used in StatusColumn component
 */
const getAvailabilityColor = (
  catalogueItem: IAugmentedCatalogItemDto & {
    displayOnRequest?: boolean
    shouldShowStock?: boolean
  }
): string => {
  if (catalogueItem.displayOnRequest) {
    return ClinTheme.colors.orangeWarn // Orange for SAGE "On Request" products
  }

  // Prioritize live P&A data to show correct color for pack counts
  const availableQuantity = catalogueItem.priceAvailability?.availableQuantity
  if (availableQuantity) {
    const quantity = Number(availableQuantity)
    if (quantity > 0) {
      return ClinTheme.colors.deepGreen // Same color as StatusColumn for in-stock items
    }
  }

  if (catalogueItem.shouldShowStock) {
    return ClinTheme.colors.deepGreen
  }

  // Fall back to ETL-based availability color
  return catalogueItem.availableColour || ClinTheme.colors.darkGrey
}

export const VariantCard: FunctionComponent<IVariantCardProps> = ({
  catalogueItem,
  countryCode,
  onClick,
  cta,
  children,
  onClickDelete,
  isDeleted,
  showGBMinimumOrderMessage
}) => {
  const { t } = useTranslation()
  const eDate = catalogueItem.expectedDeliveryDates?.find(
    (e) => e.shipToCountry === countryCode
  )
  const expectedDeliveryDate = eDate?.expectedDeliveryDate
  const cutOffTime = eDate?.shipmentCutOffTime
  const isPastCutOffTime =
    DateTime.now().toUTC() >
    DateTime.fromISO(cutOffTime || '', {
      zone: 'utc'
    })
  return (
    <ClinTwoColumnCard
      onClick={(event) => onClick && onClick(event)}
      cta={cta}
      isDeleted={isDeleted}
      left={
        <DrugPropertyGrid
          catalogueItem={catalogueItem}
          showUnavailable={getUnavailableStatus(catalogueItem)}
        >
          {children}
        </DrugPropertyGrid>
      }
      right={
        <>
          <ClinText
            as="div"
            color={ClinTheme.colors.primary}
            fontWeight={ClinTheme.fontWeights.medium}
          >
            {t('glossary:product_code')}:
            {onClickDelete && (
              <ClinButtonIcon
                iconName={ClinIconPathName.Cross}
                onClick={(e) => {
                  e.stopPropagation()
                  onClickDelete()
                }}
                hoverColor={ClinTheme.colors.primaryMid}
              />
            )}
          </ClinText>
          <ClinText
            as="div"
            variant={TypographyVariant.LargeParagraph}
            color={ClinTheme.colors.primary}
            marginTop={ClinTheme.space[1]}
            marginBottom={ClinTheme.space[3]}
          >
            {catalogueItem.sku}
          </ClinText>
          {/*<>{JSON.stringify(catalogueItem, null, 2)}</>*/}
          {/* we don't want to display availability for aus user where product division is sp - CLOS-2750 */}
          {shouldDisplaAvailability(catalogueItem, countryCode!) && (
            <ClinText
              as="div"
              color={getAvailabilityColor(catalogueItem)}
              fontSize={ClinTheme.fontSizes[2]} //have changed font size and font weight for availability according to changes in design on 03.08.23
              fontWeight={ClinTheme.fontWeights.medium}
              marginBottom={ClinTheme.space[3]}
            >
              {(() => {
                // Prioritize live P&A data to show pack counts
                const availableQuantity =
                  catalogueItem.priceAvailability?.availableQuantity
                if (availableQuantity) {
                  const quantity = Number(availableQuantity)
                  if (quantity > 0) {
                    return `${quantity} pack${
                      quantity === 1 ? '' : 's'
                    } available`
                  }
                }

                // Fall back to existing logic
                if (catalogueItem.displayOnRequest) {
                  return 'Available'
                }

                // For shouldShowStock, still use getAvailabilityText to show pack counts from availableText
                // This ensures pack counts are displayed when available
                return getAvailabilityText(catalogueItem)
              })()}
            </ClinText>
          )}
          {/* Estimated delivery date for non australia users */}
          {countryCode !== CountryAlphaCodes.Australia &&
            shouldDisplayEstimatedDeliveryDate(
              expectedDeliveryDate,
              isPastCutOffTime,
              catalogueItem
            ) && (
              <EstimatedDeliveryDate
                expectedDeliveryDate={expectedDeliveryDate}
                cutOffTime={cutOffTime}
              />
            )}
          {/* we have different design for austraia users estimated delivery date */}
          {countryCode === CountryAlphaCodes.Australia &&
            catalogueItem.item.sourceSystem === ProductSourceSystem.Sagex3 &&
            shouldDisplayEstimatedDeliveryDateForAus(catalogueItem) && (
              <EstimatedDeliveryDateAus
                isLimitedAvailable={catalogueItem.item.supplyProblem === 'Y'}
                stockLevel={
                  catalogueItem.reservableStock
                    ? catalogueItem.reservableStock
                    : 0
                }
              ></EstimatedDeliveryDateAus>
            )}
          <ClinSpacer />
          {showGBMinimumOrderMessage &&
            catalogueItem.uiVariantState ===
              UIVariationState.AvailableAccurateStockLevel &&
            !catalogueItem.cannotOrder && (
              <ClinText
                as="div"
                marginTop={-ClinTheme.space[3]}
                marginBottom={ClinTheme.space[3]}
              >
                {t('product_variants:minimum_order_quantity_warning')}
              </ClinText>
            )}
          {/* For australian users we always display estimated delivery (1-3 days if we have items in stock, and 7-14 if items are out of stock or 0) */}
          {countryCode !== CountryAlphaCodes.Australia &&
            catalogueItem.item.sourceSystem !== ProductSourceSystem.Sagex3 && (
              <ClinText as="div" marginBottom={ClinTheme.space[3]}>
                {catalogueItem.dispatchText}
              </ClinText>
            )}
          <ClinText
            as="div"
            color={ClinTheme.colors.primaryMid}
            marginBottom={ClinTheme.space[5]}
          >
            {catalogueItem.item.maximumOrderQuantity &&
              t('product_variants:maximum_order_quantity_warning', {
                maximumOrderQuantity: catalogueItem.item.maximumOrderQuantity
              })}
          </ClinText>
        </>
      }
    />
  )
}
